// src/rms/ads/campaignService.js
// ============================================================================
// Campaign persistence, shared by the staff console and the advertiser portal
// ============================================================================
// Both surfaces save the same shape, so the write path lives here once. The
// difference is authority, and it is applied here as well as in RLS:
//
//   staff       may set status and approve creative
//   advertiser  submits drafts; creative always re-enters review on edit
//
// Re-entering review on every edit is deliberate. Approving a creative and then
// letting its image URL be swapped afterwards would make the review meaningless.
// ============================================================================

import db from "../data";
import { createSuccessResponse, createErrorResponse } from "../utils/response";

const toCents = (value) => (value === "" || value == null ? null : Math.round(Number(value) * 100));
const toInt = (value) => (value === "" || value == null ? null : Number(value));

/** Splits a form creative into its stored columns and its type-specific payload. */
function creativeRow(campaignId, creative, { canApprove }) {
  const { id, name, creative_type: type, iab_size: size, click_url: clickUrl, review_status: review } = creative;

  const payload =
    type === "image"
      ? { imageUrl: creative.imageUrl, alt: creative.name, clickUrl }
      : type === "html"
      ? { html: creative.html, alt: creative.name }
      : {
          headline: creative.headline,
          body: creative.body,
          cta: creative.cta || "Learn more",
          accent: creative.accent || "#0f766e",
        };

  const size_ = { leaderboard: [728, 90], medium_rectangle: [300, 250], large_rectangle: [336, 280], half_page: [300, 600], mobile_banner: [320, 50], wide_skyscraper: [160, 600] }[size] || [300, 250];

  return {
    id,
    campaign_id: campaignId,
    name,
    creative_type: type,
    iab_size: size,
    width_px: size_[0],
    height_px: size_[1],
    payload,
    click_url: clickUrl || null,
    weight: 100,
    is_active: true,
    // Only staff may carry an approval forward. Anything an advertiser touches
    // goes back into review.
    review_status: canApprove ? review || "pending" : "pending",
  };
}

/**
 * Creates or updates a campaign and its creatives.
 *
 * `role` is "staff" or "advertiser". An advertiser can never write a status
 * beyond pending_approval, whatever the form sends.
 */
export async function saveCampaign(draft, { role = "staff" } = {}) {
  const isStaff = role === "staff";

  // An affiliate belongs to no advertiser. Nobody bought the slot -- we placed
  // a network's link in it -- so there is no advertiser record, no insertion
  // order and no budget. Inventing an advertiser to satisfy this check would
  // put a non-buyer into avails and pacing as though they were being billed.
  if (!draft.is_affiliate && !draft.advertiser_id) {
    return createErrorResponse("A campaign must belong to an advertiser.");
  }
  if (!draft.ad_categories?.length) return createErrorResponse("Choose at least one vertical.");
  if (!draft.creatives?.length) return createErrorResponse("Add at least one creative.");

  const status = isStaff ? draft.status : draft.id ? "pending_approval" : "pending_approval";

  const payload = {
    advertiser_id: draft.is_affiliate ? null : draft.advertiser_id,
    is_affiliate: !!draft.is_affiliate,
    name: draft.name.trim(),
    ad_categories: draft.ad_categories,
    placements: draft.placements,
    starts_at: draft.starts_at ? new Date(draft.starts_at).toISOString() : null,
    ends_at: draft.ends_at ? new Date(`${draft.ends_at}T23:59:59`).toISOString() : null,
    pacing: draft.pacing || "even",
    frequency_cap_per_day: toInt(draft.frequency_cap_per_day),
    status,
    is_active: true,
  };

  // Commercial terms are staff-only. Omitting them on an advertiser save means
  // an existing budget cannot be edited away from the portal.
  if (isStaff) {
    // An affiliate earns per click and per conversion, and the conversion is
    // counted by the network rather than by us. Zeroing CPM keeps it out of
    // any revenue figure computed from impressions -- it would otherwise
    // report money that nobody agreed to pay.
    payload.budget_cents = draft.is_affiliate ? null : toCents(draft.budget);
    payload.goal_impressions = draft.is_affiliate ? null : toInt(draft.goal_impressions);
    payload.cpm_cents = draft.is_affiliate ? 0 : toInt(draft.cpm) || 0;
    payload.cpc_cents = toInt(draft.cpc) || 0;
  }

  let campaign;
  if (draft.id) {
    campaign = (await db.update("adCampaigns", draft.id, payload)).data;
  } else {
    campaign = (
      await db.create("adCampaigns", {
        ...payload,
        spent_cents: 0,
        delivered_impressions: 0,
        weight: 100,
        is_house_ad: false,
      })
    ).data;
  }

  if (!campaign) return createErrorResponse("Could not save the campaign.");

  // Reconcile creatives: update existing, insert new, remove deleted.
  const existing = (await db.list("adCreatives", { campaign_id: campaign.id })).data || [];
  const keptIds = new Set(draft.creatives.filter((c) => c.id).map((c) => c.id));

  for (const stale of existing.filter((c) => !keptIds.has(c.id))) {
    await db.remove("adCreatives", stale.id);
  }

  for (const creative of draft.creatives) {
    const row = creativeRow(campaign.id, creative, { canApprove: isStaff });
    if (row.id) {
      const { id, ...patch } = row;
      await db.update("adCreatives", id, patch);
    } else {
      const { id, ...insert } = row;
      await db.create("adCreatives", insert);
    }
  }

  return createSuccessResponse(campaign);
}

/** Campaigns plus their creatives, for a given advertiser or all of them. */
export async function listCampaignsWithCreatives(advertiserId = null) {
  const campaigns = (await db.list("adCampaigns", advertiserId ? { advertiser_id: advertiserId } : {})).data || [];
  const creatives = (await db.list("adCreatives", {})).data || [];

  return campaigns
    .filter((c) => c.advertiser_id)
    .map((c) => ({
      ...c,
      creatives: creatives.filter((cr) => cr.campaign_id === c.id),
    }));
}

/** Delivery totals for one campaign. */
export async function campaignPerformance(campaignId) {
  const rows = (await db.list("adDeliveryDaily", { campaign_id: campaignId })).data || [];

  const impressions = rows.reduce((s, r) => s + (r.viewable_impressions || 0), 0);
  const clicks = rows.reduce((s, r) => s + (r.clicks || 0), 0);
  const spend = rows.reduce((s, r) => s + (r.spend_cents || 0), 0);

  return {
    impressions,
    clicks,
    spendCents: spend,
    ctr: impressions ? (clicks / impressions) * 100 : 0,
    // Effective CPM actually paid, which is what a buyer checks against the rate
    // they were quoted.
    ecpmCents: impressions ? Math.round((spend / impressions) * 1000) : 0,
    daily: rows.slice().sort((a, b) => String(a.day).localeCompare(String(b.day))),
  };
}

const campaignService = { saveCampaign, listCampaignsWithCreatives, campaignPerformance };
export default campaignService;
